import * as React from 'react';
import { twMerge } from 'tailwind-merge';

interface ExpandingInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  /** Optional row number shown to the left */
  index?: number;
  onRemove?: () => void;
  disabled?: boolean;
  mono?: boolean;
  className?: string;
  /** Variant for destructive/warning list items */
  destructive?: boolean;
  /** Name for form integration */
  name?: string;
  /** onBlur for form integration */
  onBlur?: () => void;
  /** When false, renders a single-line fixed-height input. Default true. */
  expanding?: boolean;
}

export const ExpandingInput = React.forwardRef<HTMLTextAreaElement, ExpandingInputProps>(
  (
    {
      value: controlledValue,
      onChange,
      placeholder = 'Add criteria...',
      label,
      index,
      onRemove,
      disabled = false,
      mono = false,
      className,
      destructive = false,
      name,
      onBlur,
      expanding = true,
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = React.useState(controlledValue || '');
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const internalRef = React.useRef<HTMLTextAreaElement>(null);

    const value = controlledValue !== undefined ? controlledValue : internalValue;

    const resize = React.useCallback(() => {
      const el = internalRef.current;

      if (!el || !expanding) return;
      el.style.height = '0';
      el.style.height = Math.max(22, el.scrollHeight) + 'px';
    }, [expanding]);

    React.useEffect(() => {
      resize();
    }, [value, resize]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const v = e.target.value;

      if (controlledValue === undefined) setInternalValue(v);
      onChange?.(v);
      resize();
    };

    const isExpanded = isFocused || value.length > 0;

    const mergedRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        (internalRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;

        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      },
      [ref],
    );

    return (
      <div
        className={twMerge(
          'relative flex items-start gap-3',
          disabled && 'opacity-50',
          'transition-opacity duration-200',
          className,
        )}
      >
        {index !== undefined && (
          <span
            className={twMerge(
              'min-w-[18px] pt-[11px] text-right font-mono text-[11px] tracking-[.02em] transition-colors duration-200 select-none',
              isFocused ? (destructive ? 'text-red-9' : 'text-primary-9') : 'text-text-2',
            )}
          >
            {String(index).padStart(2, '0')}
          </span>
        )}

        <div
          className={twMerge(
            'relative flex-1 cursor-text rounded-[6px] border-[1.5px] transition-all duration-200',
            isFocused
              ? twMerge(
                  'border-primary-9 bg-surface-1 shadow-[0_0_0_3px_var(--color-primary-3),0_1px_3px_rgba(0,0,0,0.04)]',
                  destructive && 'border-red-9 shadow-[0_0_0_3px_rgba(232,93,42,0.12),0_1px_3px_rgba(0,0,0,0.04)]',
                )
              : isHovered
                ? 'border-stroke-2 bg-surface-2 shadow-[0_1px_2px_rgba(0,0,0,0.02)]'
                : twMerge(
                    'border-stroke-1 bg-surface-2 shadow-[0_1px_2px_rgba(0,0,0,0.02)]',
                    destructive && 'border-l-red-9 border-l-2',
                  ),
            disabled && 'cursor-default',
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => internalRef.current?.focus()}
        >
          {label && (
            <div
              className={twMerge(
                'pointer-events-none absolute left-[11px] z-10 font-mono tracking-[.08em] uppercase transition-all duration-200',
                isExpanded
                  ? twMerge(
                      'top-[-8px] px-1 text-[9px]',
                      isFocused ? (destructive ? 'text-red-9' : 'text-primary-9') : 'text-text-3',
                      isFocused ? 'bg-surface-1' : 'bg-surface-2',
                    )
                  : 'text-text-3 top-[10px] text-[12px]',
              )}
            >
              {label}
            </div>
          )}

          <textarea
            ref={mergedRef}
            name={name}
            value={value}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              onBlur?.();
            }}
            placeholder={!label || isExpanded ? placeholder : ''}
            disabled={disabled}
            rows={1}
            className={twMerge(
              'text-text-1 w-full resize-none border-none bg-transparent px-3.5 py-2.5 text-[13.5px] leading-relaxed outline-none',
              label && 'pr-9',
              mono && 'font-mono text-[12px]',
              !expanding && 'overflow-hidden',
            )}
            style={{
              minHeight: '22px',
              overflow: expanding ? 'hidden' : undefined,
            }}
          />
        </div>

        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="hover:bg-red-9/10 mt-1.5 flex size-7 shrink-0 cursor-pointer items-center justify-center rounded border-none bg-transparent transition-all duration-150"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                className="text-text-3 [button:hover>&]:text-red-9 transition-colors duration-150"
              />
            </svg>
          </button>
        )}
      </div>
    );
  },
);
ExpandingInput.displayName = 'ExpandingInput';
